import { Platform } from 'react-native';

// Scroll restoration for client-side navigation (web only), imported once by
// each app's root layout for its side effects.
//
// expo-router restores no scroll position on web, and the apps scroll in two
// different places: dubai's pages are plain DOM, so the document scrolls;
// riviera's each scroll inside their own react-native ScrollView (body is
// overflow:hidden, ScrollViewStyleReset). Left alone, dubai kept the previous
// page's window.scrollY after a link, and riviera, whose screens remount, lost
// the position on back/forward. So: a push to a new pathname starts at the top,
// and back/forward returns to where that history entry was left. The browser's
// own restoration is off — for the document it runs while the previous (often
// shorter) page is still rendered, and is clamped to its height. A reload is
// restored from sessionStorage.
//
// Navigations are told apart at the history API, not by usePathname: on
// back/forward it can report a stale pathname for a render, or skip one.

// The page's vertical scroller: a ScrollView spanning most of the viewport
// (riviera), else the document (dubai). Carousels and dropdowns are short.
function isPageScroller(el: Element): boolean {
  if (el === document.scrollingElement) return true;
  if (el.clientHeight < window.innerHeight / 2 || el.scrollHeight <= el.clientHeight) return false;
  return /(auto|scroll)/.test(getComputedStyle(el).overflowY);
}

function pageScroller(): Element | null {
  let best: Element | null = null;
  for (const el of document.querySelectorAll('div')) {
    if ((el as HTMLElement).offsetParent !== null && isPageScroller(el) && (!best || el.clientHeight > best.clientHeight)) {
      best = el;
    }
  }
  return best ?? document.scrollingElement;
}

// Scroll position per history entry, by the id react-navigation keeps in history.state.
const positions = new Map<string, number>();
const entryId = (): string | undefined => (history.state as { id?: string } | null)?.id;
const STORAGE_KEY = 'scroll-positions';

// Where the visitor is: the current entry and its last scroll position.
let currentId: string | undefined;
let currentY = 0;

// Holds the position for ~1.5 s: the page may not be rendered (or tall enough)
// yet, and swapping the screens briefly drops it to the top. Stops as soon as
// the visitor scrolls themselves. Timers, not requestAnimationFrame, which a
// background tab doesn't run.
let retry: ReturnType<typeof setTimeout> | undefined;
function stopRestore() {
  clearTimeout(retry);
  retry = undefined;
}
function restore(y: number) {
  stopRestore();
  let tries = 90;
  const step = () => {
    const el = pageScroller();
    if (el && Math.abs(el.scrollTop - y) > 1) el.scrollTop = y;
    retry = --tries > 0 ? setTimeout(step, 16) : undefined;
  };
  step();
}

function leave() {
  if (currentId) positions.set(currentId, currentY);
}

if (Platform.OS === 'web' && typeof window !== 'undefined') {
  history.scrollRestoration = 'manual';
  try {
    for (const [id, y] of JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]') as [string, number][]) {
      positions.set(id, y);
    }
  } catch {}

  // A reload (or a return from another site) keeps the entry's history.state;
  // a fresh visit has none yet, and a #hash is left to the browser.
  currentId = entryId();
  const saved = currentId ? positions.get(currentId) : undefined;
  if (saved && !location.hash) {
    currentY = saved;
    restore(saved);
  }

  // Capture: a ScrollView's scroll event doesn't bubble.
  window.addEventListener(
    'scroll',
    (event) => {
      const el = event.target === document ? document.scrollingElement : event.target;
      if (el instanceof Element && isPageScroller(el)) currentY = el.scrollTop;
    },
    { capture: true, passive: true },
  );
  for (const type of ['wheel', 'touchstart', 'keydown', 'mousedown']) {
    window.addEventListener(type, stopRestore, { passive: true });
  }

  // A push. react-navigation calls it right after rendering the new page, in
  // the same task, so currentY is still the page being left.
  const pushState = history.pushState.bind(history);
  history.pushState = (data, unused, url) => {
    const from = location.pathname;
    leave();
    pushState(data, unused, url);
    currentId = entryId();
    // A query change on the same page (a hub's filters, dubai's ?q=) keeps the scroll.
    if (location.pathname === from) return;
    currentY = 0;
    stopRestore();
    // A new screen's ScrollView starts at the top; the document doesn't.
    window.scrollTo(0, 0);
  };
  // react-navigation also rewrites the current entry's state (and its id).
  const replaceState = history.replaceState.bind(history);
  history.replaceState = (data, unused, url) => {
    replaceState(data, unused, url);
    currentId = entryId();
  };

  // Back/forward: history.state is already the arrived-at entry.
  window.addEventListener('popstate', () => {
    leave();
    currentId = entryId();
    currentY = (currentId && positions.get(currentId)) || 0;
    restore(currentY);
  });

  window.addEventListener('pagehide', () => {
    leave();
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...positions].slice(-50)));
    } catch {}
  });
}

export {};
